/**
 * Country data module - single source of truth for all country information.
 *
 * This module consolidates:
 * - Country definitions with details and map hints
 * - Name mappings for Natural Earth atlas
 * - Geographic and thematic region groupings
 * - Utility functions for lookups
 */

// Re-export types
export * from './types';

// Re-export regions
export * from './regions';

// Re-export scopes
export * from './scopes';

// Re-export name mappings
export {
  atlasNameToCode,
  displayNameToAtlasName,
  codeToAtlasName,
  getAtlasName,
  getCodeFromAtlasName,
} from './name-mappings';

// Descriptions and photos are now stored in the database and fetched via API
// Use the CountryDataContext to access them

// Import country arrays
import { europeCountries } from './europe';
import { asiaCountries } from './asia';
import { northAmericaCountries, southAmericaCountries, americasCountries } from './americas';
import { oceaniaCountries } from './oceania';
import { africaCountries } from './africa';

import type { Country, CountryCode, CountryDetails, Rating, RatingLevel } from './types';

// Re-export individual region arrays for convenience
export {
  europeCountries,
  asiaCountries,
  northAmericaCountries,
  southAmericaCountries,
  americasCountries,
  oceaniaCountries,
  africaCountries,
};

/**
 * All countries combined into a single array.
 * This is the primary export for iterating through countries.
 */
export const countries: Country[] = [
  ...europeCountries,
  ...asiaCountries,
  ...americasCountries,
  ...oceaniaCountries,
  ...africaCountries,
];

/**
 * Country lookup map by ISO code.
 * O(1) access to any country by code.
 */
export const countryByCode: Map<CountryCode, Country> = new Map(
  countries.map((c) => [c.code, c])
);

/**
 * Country lookup map by display name.
 * O(1) access to any country by name.
 */
export const countryByName: Map<string, Country> = new Map(
  countries.map((c) => [c.name, c])
);

/**
 * Get a country by its ISO code.
 */
export function getCountryByCode(code: CountryCode): Country | undefined {
  return countryByCode.get(code);
}

/**
 * Get a country by its display name.
 */
export function getCountryByName(name: string): Country | undefined {
  return countryByName.get(name);
}

/**
 * Get country details by ISO code.
 * Returns undefined if the country or its details don't exist.
 */
export function getCountryDetails(code: CountryCode): CountryDetails | undefined {
  return countryByCode.get(code)?.details;
}

/**
 * Get countries filtered by continent.
 */
export function getCountriesByContinent(continent: string): Country[] {
  return countries.filter((c) => c.continent === continent);
}

/**
 * Get countries filtered by subregion.
 */
export function getCountriesBySubregion(subregion: string): Country[] {
  return countries.filter((c) => c.subregion === subregion);
}

/**
 * Get all unique continents.
 */
export function getContinents(): string[] {
  return [...new Set(countries.map((c) => c.continent))];
}

/**
 * Get all unique subregions.
 */
export function getSubregions(): string[] {
  return [...new Set(countries.map((c) => c.subregion).filter(Boolean))] as string[];
}

/**
 * Rating labels and descriptions.
 * Used for UI display of rating options.
 */
export const ratingLabels: Record<Rating, RatingLevel> = {
  1: {
    label: 'Never',
    description: 'I would never live here',
    color: 'text-red-600',
  },
  2: {
    label: 'Reluctantly',
    description: 'I would be reluctant to live here',
    color: 'text-orange-500',
  },
  3: {
    label: 'Maybe',
    description: 'I might consider living here',
    color: 'text-yellow-500',
  },
  4: {
    label: 'Willing',
    description: 'I would be willing to live here',
    color: 'text-lime-500',
  },
  5: {
    label: 'Absolutely',
    description: 'I would absolutely love to live here',
    color: 'text-emerald-600',
  },
};

/**
 * Get rating color for map visualization.
 */
export function getRatingColor(rating: number | undefined): string {
  if (rating === undefined) return '#9CA3AF'; // gray-400

  const colors: Record<number, string> = {
    1: '#DC2626', // red-600
    2: '#F97316', // orange-500
    3: '#FBBF24', // yellow-400
    4: '#84CC16', // lime-500
    5: '#059669', // emerald-600
  };

  return colors[rating] ?? '#9CA3AF';
}

/**
 * Legacy compatibility: countryDetails object for existing code.
 * Maps country code to details object.
 */
export const countryDetails: Record<string, CountryDetails> = Object.fromEntries(
  countries
    .filter((c) => c.details)
    .map((c) => [c.code, c.details!])
);
